import {filler, fragment, hframe, input} from '@xh/hoist/cmp/layout';
import {hoistCmp, XH} from '@xh/hoist/core';
import {panel} from '@xh/hoist/desktop/cmp/panel';
import {PositionsModel} from './PositionsModel';
import {toolbar, toolbarSep} from '@xh/hoist/desktop/cmp/toolbar';
import {button, colAutosizeButton, exportButton} from '@xh/hoist/desktop/cmp/button';
import {Icon} from '@xh/hoist/icon';
import {positionImporter} from './PositionImporter';
import {ExtendedIconMap} from '../../../../../core/IconUtils';
import {colChooserButton} from '@xh/hoist/desktop/cmp/button';
import {storeFilterField} from '@xh/hoist/cmp/store';
import {orderTicketDialog} from '../orders/OrderTicket';
import {useOnVisibleChange} from '@xh/hoist/utils/react';
import {useComposedRefs} from '@xh/hoist/utils/react/ComposeRefs';
import {transactionsTab} from '../transactions/TransactionsTab';
import {configurableGrid} from '../../../../cmp/gridSpec/ConfigurableGrid';
import {GridSpecSizingMode} from '../../../../cmp/gridSpec/GridSpecTypes';

const SIZES: GridSpecSizingMode[] = ['tiny', 'compact', 'standard', 'large'];

export const positions = hoistCmp.factory<PositionsModel>({
    render({model}) {
        const visibleRef = useOnVisibleChange(visible => (model.visible = visible)),
            panelRef = useComposedRefs(model.panelRef, visibleRef);
        return fragment(
            panel({
                ref: panelRef,
                item: hframe(
                    positionsTab(),
                    transactionsTab({model: model.transactionsModel}),
                    orderTicketDialog({model: model.orderTicketModel}),
                    positionImporter({model: model.importer})
                ),

                hotkeys: [
                    {
                        global: true,
                        label: 'New Order',
                        combo: 'ctrl + O',
                        onKeyDown: () => {
                            if (model.visible && !XH.entitlementService.isShadow) model.newOrder();
                        },
                        stopPropagation: true,
                        preventDefault: true
                    }
                ]
            })
        );
    }
});

const positionsTab = hoistCmp.factory<PositionsModel>(({model}) =>
    panel({
        testId: 'positions-panel',
        tbar: tbar(),
        item: configurableGrid({model: model.configurableGridModel, testId: 'positions-grid'}),
        mask: XH.livePositionsService.apiTask
    })
);

const tbar = hoistCmp.factory<PositionsModel>(({model}) => {
    const {gridModel, configurableGridModel} = model,
        {specModel} = configurableGridModel,
        sizeIdx = SIZES.indexOf((specModel.spec.sizingMode ?? gridModel.sizingMode) as GridSpecSizingMode),
        setSize = (sizingMode: GridSpecSizingMode) => specModel.dispatch([{op: 'setOptions', options: {sizingMode}}], {source: 'manual'});

    return toolbar({
        compact: true,
        items: [
            button({
                icon: Icon.gear(),
                tooltip: 'Configure (with AI assistant)',
                onClick: () => model.openConfigurator()
            }),
            colAutosizeButton({gridModel, icon: Icon.arrowsLeftRight()}),
            colChooserButton({gridModel}),
            button({
                title: 'Restore Defaults',
                icon: Icon.reset(),
                onClick: () => specModel.reset()
            }),
            button({
                title: 'Make Larger',
                icon: ExtendedIconMap.TEXT_SIZE_INCREASE,
                disabled: sizeIdx >= SIZES.length - 1,
                onClick: () => setSize(SIZES[sizeIdx + 1])
            }),
            button({
                title: 'Make Smaller',
                icon: ExtendedIconMap.TEXT_SIZE_DECREASE,
                disabled: sizeIdx <= 0,
                onClick: () => setSize(SIZES[sizeIdx - 1])
            }),
            exportButton({gridModel}),
            button({
                title: 'Clear filters',
                intent: 'danger',
                omit: model.gridFilter == null,
                icon: Icon.filter({}),
                onClick: () => model.clearFilters()
            }),
            toolbarSep(),
            button({
                icon: Icon.chartArea(),
                tooltip: 'Transactions',
                disabled: !model.selectedPosition,
                onClick: () => model.showTransactions()
            }),
            // A shadow user is read-only: no orders, no imports
            button({
                icon: Icon.book(),
                intent: 'primary',
                tooltip: 'New Order',
                omit: XH.entitlementService.isShadow,
                onClick: () => model.newOrder()
            }),
            button({
                icon: Icon.cloudDownload(),
                tooltip: 'Import Transactions',
                omit: XH.entitlementService.isShadow,
                onClick: () => model.openFile()
            }),
            input({
                omit: XH.entitlementService.isShadow,
                type: 'file',
                ref: model.filePickerRef,
                style: {display: 'none'},
                onChange: e => model.import(e.target.files[0])
            }),
            filler(),
            storeFilterField({gridModel, matchMode: 'any'})
        ]
    });
});
